<script>
  import { createEventDispatcher, onDestroy, tick } from "svelte";
  import Modal from "lib/components/Modal.svelte";
  import { Button } from "paxray-components-svelte";
  import { DOWNLOAD_ICON } from "@utils/icons.js";
  import BpmnInlinePreview from "./BpmnInlinePreview.svelte";
  import BpmnInlineModeler from "./BpmnInlineModeler.svelte";
  import BpmnDrawAssistant from "./BpmnDrawAssistant.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { formatTimeDelta } from "lib/util/formatfunctions.js";
  import { recordingStore } from "@stores/recordingStore.js";

  export let open = false;
  export let graphMode = "transitions";
  export let nodes = [];
  export let edges = [];
  export let variants = [];
  export let selectedVariantIds = [];
  export let taskName = "Object Process";
  export let level = "L3";
  export let idproject = "";
  export let recordTypes = [];
  export let selectedStartNodes = [];
  export let savedDrawings = [];

  const dispatch = createEventDispatcher();

  let topKMode = "balanced";
  let topKCount = 3;
  let topKCoverage = 80;
  const showGatewayBehaviorSelection = false;
  let gatewayBehavior = "split_xor";
  let includeDatabases = true;
  let includeDocuments = true;
  let includeAvgTime = false;
  let includeFrequency = false;
  let includeL4Subprocesses = false;
  let includeThroughputTime = false;
  let throughputRecordType = "";
  let artifactMode = "custom";
  let systemMinFrequency = 0;
  let fileMinFrequency = 0;
  let selectedActivities = [];
  let selectedDrawTaskIds = [];
  let drawTaskSearch = "";
  let drawTaskGrouping = "Team";
  let drawCatalogLoading = false;
  let taskSelectionPage = 1;
  const taskSelectionPageSize = 5;
  let modalVariantIds = [];
  let sectionsOpen = {
    topK: true,
    selection: true,
    gateway: true,
    artifacts: true,
  };
  let previewXml = "";
  let previewLoading = false;
  // Was gerade laeuft - der Nutzer sieht sonst nur ein stehendes Bild.
  let previewLoadingText = "Generating BPMN Export";
  let previewError = "";
  let generating = false;
  // Ausgabeformat: bewusst erst hier, am Ende des gefuehrten Ablaufs.
  let exportFormat = "standard";
  let orientation = "horizontal";
  let bicIdentifier = "";
  let bicValidTo = "";
  let bicChangeLog = "";
  let bicGenerateTexts = false;
  let previewTimer;
  let previewRequest = 0;
  let initializedOpen = false;
  let lastPreviewKey = "";
  let subtaskCatalog = [];
  let subtaskCatalogProject = "";
  const subprocessGraphCache = new Map();
  let inlineModeler;
  let drawProcessName = "Generated Process";
  let drawDiagramState = {
    started: false,
    activities: [],
    gateways: 0,
    hasEnd: false,
  };
  let drawPostprocessed = false;
  let drawSaved = false;
  let saveDrawingOpen = false;
  let saveDrawingName = "";
  let drawRecordTaskIds = [];
  let drawSourceGraph = null;

  $: drawMode = level === "DRAW";
  $: if (
    recordTypes.length &&
    !recordTypes.some((item) => item.name === throughputRecordType)
  )
    throughputRecordType = recordTypes[0].name;
  $: drawFramework = {
    L1: ($recordingStore.processes || []).map((item) => ({
      id: item.id,
      name: item.name,
    })),
    L2: ($recordingStore.subprocesses || []).map((item) => ({
      id: item.id,
      name: item.name,
    })),
    L3: ($recordingStore.tasks || []).map((item) => ({
      id: item.id,
      name: item.name,
    })),
  };

  $: activityNodes = nodes.filter(
    (node) => node.id !== "Start" && node.id !== "End" && node.parentNode,
  );
  $: activityLabels = activityNodes.map((node) => node.label || node.id);
  $: selectedDrawTasks = subtaskCatalog.filter((task) =>
    selectedDrawTaskIds.includes(task.id),
  );
  $: drawTaskRows = (drawTaskGrouping, subtaskCatalog)
    .filter((task) =>
      normalizedName(task.name).includes(normalizedName(drawTaskSearch)),
    )
    .sort(
      (a, b) =>
        drawTaskGroup(a).localeCompare(drawTaskGroup(b)) ||
        a.name.localeCompare(b.name),
    );
  $: taskRows = drawMode
    ? drawTaskRows
    : activityNodes.map((node) => ({
        id: node.id,
        label: node.label || node.id,
        cases: Number(node.metrics?.occurrence_rate),
      }));
  $: taskSelectionPageCount = Math.max(
    1,
    Math.ceil(taskRows.length / taskSelectionPageSize),
  );
  $: if (taskSelectionPage > taskSelectionPageCount)
    taskSelectionPage = taskSelectionPageCount;
  $: visibleTaskRows = taskRows.slice(
    (taskSelectionPage - 1) * taskSelectionPageSize,
    taskSelectionPage * taskSelectionPageSize,
  );
  $: visibleDrawTaskGroups =
    (drawTaskGrouping, groupDrawTasks(visibleTaskRows));
  $: effectiveTopKCount =
    topKMode === "simple" ? 1 : topKMode === "balanced" ? 3 : topKCount;
  $: effectiveTopKCoverage =
    topKMode === "simple" ? 60 : topKMode === "balanced" ? 80 : topKCoverage;
  $: effectiveSystemMin =
    artifactMode === "simple"
      ? 10
      : artifactMode === "balanced"
        ? 20
        : systemMinFrequency;
  $: effectiveFileMin =
    artifactMode === "simple"
      ? 10
      : artifactMode === "balanced"
        ? 20
        : fileMinFrequency;
  $: sortedVariants = [...variants].sort(
    (a, b) => Number(b.count || 0) - Number(a.count || 0),
  );
  $: selectedVariants = selectTopVariants(
    sortedVariants.filter((variant) => modalVariantIds.includes(variant.id)),
    effectiveTopKCount,
    effectiveTopKCoverage,
    sortedVariants,
  );
  $: selectedActivitySet = new Set(selectedActivities);
  $: teamName =
    [
      ...new Set(
        (drawMode
          ? selectedDrawTasks.flatMap((task) => task.teams || [])
          : activityNodes
              .filter((node) => selectedActivitySet.has(node.label || node.id))
              .map((node) => node.parentNode)
        ).filter(Boolean),
      ),
    ].join(", ") || null;
  $: selectedEdges = drawMode
    ? buildDrawEdges(selectedDrawTasks)
    : buildSelectedEdges(
        graphMode,
        edges,
        selectedActivitySet,
        selectedVariants,
        effectiveTopKCount,
        effectiveTopKCoverage,
        variants,
      );
  $: previewKey =
    open && !drawMode
      ? JSON.stringify({
          graphMode,
          topKMode,
          effectiveTopKCount,
          effectiveTopKCoverage,
          gatewayBehavior,
          includeDatabases,
          includeDocuments,
          includeAvgTime,
          includeFrequency,
          includeL4Subprocesses,
          includeThroughputTime,
          throughputRecordType,
          artifactMode,
          effectiveSystemMin,
          effectiveFileMin,
          selectedActivities: [...selectedActivities].sort(),
          selectedDrawTaskIds: [...selectedDrawTaskIds].sort(),
          variants: selectedVariants.map((variant) => variant.id),
          teamName,
          level,
          includeL4Subprocesses,
          idproject,
        })
      : "";

  $: if (open && !initializedOpen) {
    initializedOpen = true;
    modalVariantIds = selectedVariantIds.length
      ? [...selectedVariantIds]
      : variants.map((variant) => variant.id);
    selectedActivities = [...activityLabels];
    selectedDrawTaskIds = [];
    drawTaskSearch = "";
    taskSelectionPage = 1;
    previewXml = "";
    previewError = "";
    lastPreviewKey = "";
    if (drawMode) loadDrawTaskCatalog();
  }
  $: if (!open) initializedOpen = false;
  $: if (previewKey && previewKey !== lastPreviewKey) {
    lastPreviewKey = previewKey;
    clearTimeout(previewTimer);
    previewTimer = setTimeout(refreshPreview, 450);
  }

  function selectTopVariants(source, count, coverage, allVariants) {
    const selected = [];
    const total = allVariants.reduce(
      (sum, variant) => sum + Number(variant.count || 0),
      0,
    );
    let covered = 0;
    for (const variant of source) {
      if (
        selected.length >= count ||
        (selected.length && total && (covered / total) * 100 >= coverage)
      )
        break;
      selected.push(variant);
      covered += Number(variant.count || 0);
    }
    return selected;
  }

  function variantPath(variant) {
    return Array.isArray(variant.path)
      ? variant.path
      : Array.isArray(variant.steps)
        ? variant.steps
        : [];
  }

  function buildVariantEdges(source, activities) {
    const weights = new Map();
    source.forEach((variant) => {
      const path = variantPath(variant).filter((step) => activities.has(step));
      const sequence = ["Start", ...path, "End"].filter(
        (step, index, values) => !index || step !== values[index - 1],
      );
      sequence.slice(0, -1).forEach((from, index) => {
        const key = `${from}\u0000${sequence[index + 1]}`;
        weights.set(key, (weights.get(key) || 0) + Number(variant.count || 1));
      });
    });
    return [...weights].map(([key, weight]) => [
      ...key.split("\u0000"),
      weight,
    ]);
  }

  function buildDrawEdges(tasks) {
    const labels = tasks.map((task) => task.name);
    const selectedLabels = new Set(labels);
    const graphEdges = edges
      .filter(
        (edge) =>
          (edge.from === "Start" || selectedLabels.has(edge.from)) &&
          (edge.to === "End" || selectedLabels.has(edge.to)),
      )
      .map((edge) => [edge.from, edge.to, Number(edge.value || 1)]);
    const presentLabels = new Set(
      graphEdges.flatMap(([from, to]) => [from, to]),
    );
    labels
      .filter((label) => !presentLabels.has(label))
      .forEach((label) => {
        graphEdges.push(["Start", label, 1], [label, "End", 1]);
      });
    return graphEdges;
  }

  function drawTaskGroup(task) {
    if (drawTaskGrouping === "Team") return task.teams?.[0] || "Other Teams";
    return task.groups?.[drawTaskGrouping]?.name || `Other ${drawTaskGrouping}`;
  }

  function groupDrawTasks(tasks) {
    const groups = [];
    tasks.forEach((task) => {
      const name = drawTaskGroup(task);
      let group = groups.find((item) => item.name === name);
      if (!group) {
        group = { name, tasks: [] };
        groups.push(group);
      }
      group.tasks.push(task);
    });
    return groups;
  }

  function edgeId(edge) {
    return `${edge.from}\u0000${edge.to}`;
  }

  function getTopPaths(sourceEdges, maxPaths = 50) {
    const outgoing = new Map();
    sourceEdges.forEach((edge) => {
      if (!outgoing.has(edge.from)) outgoing.set(edge.from, []);
      outgoing.get(edge.from).push(edge);
    });
    outgoing.forEach((items) =>
      items.sort((a, b) => Number(b.value || 0) - Number(a.value || 0)),
    );
    const paths = [];
    const queue = [{ node: "Start", nodes: ["Start"], edgeIds: [], score: 1 }];
    while (queue.length && paths.length < maxPaths) {
      queue.sort((a, b) => b.score - a.score);
      const current = queue.shift();
      if (current.node === "End") {
        paths.push(current);
        continue;
      }
      (outgoing.get(current.node) || []).forEach((edge, index) => {
        if (edge.to !== "End" && current.nodes.includes(edge.to)) return;
        queue.push({
          node: edge.to,
          nodes: [...current.nodes, edge.to],
          edgeIds: [...current.edgeIds, edgeId(edge)],
          score:
            (current.score * (Number(edge.value || 0) + 1)) /
            Math.max(index + 1, 1),
        });
      });
    }
    return paths;
  }

  function pathCoverage(edgeIds, sourceVariants) {
    const total = sourceVariants.reduce(
      (sum, variant) => sum + Number(variant.count || 0),
      0,
    );
    if (!total) return 0;
    return (
      sourceVariants.reduce((sum, variant) => {
        const sequence = ["Start", ...variantPath(variant), "End"];
        const variantEdges = sequence
          .slice(0, -1)
          .map((from, index) => `${from}\u0000${sequence[index + 1]}`);
        return variantEdges.every((id) => edgeIds.has(id))
          ? sum + Number(variant.count || 0)
          : sum;
      }, 0) / total
    );
  }

  function buildSelectedEdges(
    mode,
    sourceEdges,
    activities,
    selectedPaths,
    pathCount,
    coverage,
    sourceVariants,
  ) {
    if (mode === "variants")
      return buildVariantEdges(selectedPaths, activities);
    const availableEdges = sourceEdges.filter(
      (edge) =>
        (edge.from === "Start" || activities.has(edge.from)) &&
        (edge.to === "End" || activities.has(edge.to)),
    );
    const topPaths = getTopPaths(availableEdges);
    const chosenPaths = [];
    const chosenEdgeIds = new Set();
    for (const path of topPaths) {
      if (chosenPaths.length >= Math.max(pathCount, 1)) break;
      chosenPaths.push(path);
      path.edgeIds.forEach((id) => chosenEdgeIds.add(id));
      if (pathCoverage(chosenEdgeIds, sourceVariants) * 100 >= coverage) break;
    }
    return availableEdges
      .filter((edge) => chosenEdgeIds.has(edgeId(edge)))
      .map((edge) => [edge.from, edge.to, Number(edge.value || 1)]);
  }

  function buildArtifacts() {
    const artifacts = [];
    activityNodes
      .filter((node) => selectedActivitySet.has(node.label || node.id))
      .forEach((node) => {
        const label = node.label || node.id;
        const metrics = node.metrics || {};
        if (
          includeDatabases &&
          metrics.apps &&
          typeof metrics.apps === "object"
        ) {
          Object.entries(metrics.apps)
            .filter(
              ([, share]) => Number(share || 0) * 100 >= effectiveSystemMin,
            )
            .forEach(([name]) =>
              artifacts.push({ task: label, type: "database", name }),
            );
        }
        if (
          includeDocuments &&
          metrics.top_windowtitles &&
          typeof metrics.top_windowtitles === "object"
        ) {
          const document = Object.entries(metrics.top_windowtitles)
            .filter(([, share]) => Number(share || 0) * 100 >= effectiveFileMin)
            .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))[0];
          if (document)
            artifacts.push({
              task: label,
              type: "document",
              name: document[0],
            });
        }
      });
    return artifacts;
  }

  function buildNodeNotes() {
    if (!includeAvgTime && !includeFrequency) return null;
    const notes = {};
    activityNodes
      .filter((node) => selectedActivitySet.has(node.label || node.id))
      .forEach((node) => {
        const details = [];
        const metrics = node.metrics || {};
        if (includeAvgTime) {
          const occurrenceRate = Number(metrics.occurrence_rate);
          const timePerCase = Number(metrics.time_per_case);
          const averageTime =
            Number.isFinite(timePerCase) &&
            Number.isFinite(occurrenceRate) &&
            occurrenceRate > 0
              ? timePerCase / occurrenceRate
              : Number(metrics.averageDurationSeconds);
          details.push(
            `Avg. Time: ${Number.isFinite(averageTime) ? formatTimeDelta(averageTime) : "-"}`,
          );
        }
        if (includeFrequency)
          details.push(
            `Frequency: ${Math.round(Number(metrics.occurrence_rate || 0) * 100)}%`,
          );
        notes[node.label || node.id] = details.join("\n");
      });
    return notes;
  }

  function buildActivityMetrics() {
    if (drawMode) {
      return selectedDrawTasks
        .map((task) => ({
          activity: task.name,
          avg_time_seconds: Number(task.avg_time_seconds),
          frequency: 1,
        }))
        .filter(
          (item) => item.activity && Number.isFinite(item.avg_time_seconds),
        );
    }

    return activityNodes
      .filter((node) => selectedActivitySet.has(node.label || node.id))
      .map((node) => {
        const metrics = node.metrics || {};
        const frequency = Number(metrics.occurrence_rate);
        const weightedTime = Number(metrics.time_per_case);
        if (
          !Number.isFinite(frequency) ||
          frequency <= 0 ||
          !Number.isFinite(weightedTime)
        ) {
          return null;
        }
        return {
          activity: node.label || node.id,
          avg_time_seconds: weightedTime / frequency,
          frequency,
        };
      })
      .filter(Boolean);
  }

  function buildDrawNodeNotes() {
    if (!includeAvgTime && !includeFrequency) return null;
    const notes = {};
    selectedDrawTasks.forEach((task) => {
      const details = [];
      if (includeAvgTime) {
        const averageTime = Number(task.avg_time_seconds);
        details.push(
          `Avg. Time: ${Number.isFinite(averageTime) ? formatTimeDelta(averageTime) : "-"}`,
        );
      }
      if (includeFrequency) {
        const frequency = Number(task.monthly_frequency);
        details.push(
          `Frequency: ${Number.isFinite(frequency) ? `${Math.round(frequency)} / month` : "-"}`,
        );
      }
      notes[task.name] = details.join("\n");
    });
    return notes;
  }

  async function buildDrawArtifacts() {
    if (!includeDatabases && !includeDocuments) return [];
    return (
      await Promise.all(
        selectedDrawTasks.map(async (task) => {
          const graph = await getTaskGraph(task);
          const apps = new Map();
          const documents = new Map();
          Object.values(graph.activityKpis || {}).forEach((metrics) => {
            Object.entries(metrics.apps || {}).forEach(([name, share]) => {
              apps.set(name, Math.max(apps.get(name) || 0, Number(share || 0)));
            });
            Object.entries(metrics.top_windowtitles || {}).forEach(
              ([name, share]) => {
                documents.set(
                  name,
                  Math.max(documents.get(name) || 0, Number(share || 0)),
                );
              },
            );
          });
          const artifacts = [];
          if (includeDatabases) {
            [...apps]
              .filter(([, share]) => share * 100 >= effectiveSystemMin)
              .forEach(([name]) =>
                artifacts.push({ task: task.name, type: "database", name }),
              );
          }
          if (includeDocuments) {
            const document = [...documents]
              .filter(([, share]) => share * 100 >= effectiveFileMin)
              .sort((a, b) => b[1] - a[1])[0];
            if (document)
              artifacts.push({
                task: task.name,
                type: "document",
                name: document[0],
              });
          }
          return artifacts;
        }),
      )
    ).flat();
  }

  function normalizedName(value) {
    return String(value || "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  }

  async function getSubtaskCatalog() {
    if (subtaskCatalogProject === idproject) return subtaskCatalog;
    const response = await fetchWithSession(
      `/api/damprojects/get-relevant-subtasks?project_id=${encodeURIComponent(idproject)}&filter=[]`,
    );
    if (!response.ok)
      throw new Error(`L4 task mapping failed with status ${response.status}`);
    subtaskCatalog = await response.json();
    subtaskCatalog = Array.isArray(subtaskCatalog) ? subtaskCatalog : [];
    subtaskCatalogProject = idproject;
    subprocessGraphCache.clear();
    return subtaskCatalog;
  }

  async function loadDrawTaskCatalog() {
    drawCatalogLoading = true;
    try {
      subtaskCatalog = await getFrameworkTaskCatalog();
      selectedDrawTaskIds = [];
    } catch (error) {
      previewError =
        error.message || "Failed to load the project L4 task catalog.";
    } finally {
      drawCatalogLoading = false;
    }
  }

  async function getFrameworkTaskCatalog() {
    await recordingStore.initialize();
    const catalog = await getSubtaskCatalog();
    const processesById = Object.fromEntries(
      ($recordingStore.processes || []).map((item) => [item.id, item]),
    );
    const subprocessesById = Object.fromEntries(
      ($recordingStore.subprocesses || []).map((item) => [item.id, item]),
    );
    const tasksById = Object.fromEntries(
      ($recordingStore.tasks || []).map((item) => [item.id, item]),
    );
    return catalog.map((item) => {
      const parentTask = tasksById[item.task_id];
      const subprocess = subprocessesById[parentTask?.subprocess_id];
      const process = processesById[subprocess?.process_id];
      return {
        ...item,
        id: String(item.id),
        groups: {
          L1: { id: process?.id, name: process?.name },
          L2: { id: subprocess?.id, name: subprocess?.name },
          L3: { id: parentTask?.id, name: parentTask?.name },
        },
      };
    });
  }

  function commonFrameworkPath(tasks) {
    const paths = tasks
      .map((task) => [
        task.groups?.L1?.name,
        task.groups?.L2?.name,
        task.groups?.L3?.name,
      ])
      .filter((path) => path.every(Boolean));
    if (!paths.length) return [];

    const firstPath = paths[0];
    const common = [];
    for (let index = 0; index < firstPath.length; index += 1) {
      const name = firstPath[index];
      if (
        paths.every(
          (path) => normalizedName(path[index]) === normalizedName(name),
        )
      ) {
        common.push(name);
      } else {
        break;
      }
    }
    return common;
  }

  async function getSaveFrameworkPath() {
    const catalog = await getFrameworkTaskCatalog();
    if (drawMode) {
      const selectedIds = new Set(selectedDrawTaskIds.map(String));
      const selectedTasks = catalog.filter((task) =>
        selectedIds.has(String(task.id)),
      );
      if (selectedTasks.length !== selectedIds.size) return [];
      return commonFrameworkPath(selectedTasks);
    }

    const selectedNodes = activityNodes.filter((node) =>
      selectedActivitySet.has(node.label || node.id),
    );
    const matchedTasks = selectedNodes
      .map((node) => {
        const name = normalizedName(node.label || node.id);
        const candidates = catalog.filter(
          (task) => normalizedName(task.name || task.task || task.l4) === name,
        );
        return (
          candidates.find((task) =>
            (task.teams || []).includes(node.parentNode),
          ) || candidates[0]
        );
      })
      .filter(Boolean);
    if (matchedTasks.length !== selectedNodes.length) return [];
    return commonFrameworkPath(matchedTasks);
  }

  async function getTaskGraph(task) {
    const cacheKey = `${idproject}:${task.id}`;
    let graph = subprocessGraphCache.get(cacheKey);
    if (graph) return graph;
    const response = await fetchWithSession(
      `/api/damprojects/getProcessGraph?project_id=${encodeURIComponent(idproject)}&subtaskid=${encodeURIComponent(task.id)}&expandedNodes=[]&filter=[]`,
    );
    if (!response.ok)
      throw new Error(
        `L4 process loading failed with status ${response.status}`,
      );
    graph = await response.json();
    subprocessGraphCache.set(cacheKey, graph);
    return graph;
  }

  function buildSubprocessArtifacts(graph, graphEdges) {
    const artifacts = [];
    const labels = new Set(
      graphEdges
        .flatMap(([from, to]) => [from, to])
        .filter((name) => name !== "Start" && name !== "End"),
    );
    labels.forEach((label) => {
      const metrics = graph.activityKpis?.[label] || {};
      if (
        includeDatabases &&
        metrics.apps &&
        typeof metrics.apps === "object"
      ) {
        Object.entries(metrics.apps)
          .filter(([, share]) => Number(share || 0) * 100 >= effectiveSystemMin)
          .forEach(([name]) =>
            artifacts.push({ task: label, type: "database", name }),
          );
      }
      if (
        includeDocuments &&
        metrics.top_windowtitles &&
        typeof metrics.top_windowtitles === "object"
      ) {
        const document = Object.entries(metrics.top_windowtitles)
          .filter(([, share]) => Number(share || 0) * 100 >= effectiveFileMin)
          .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))[0];
        if (document)
          artifacts.push({ task: label, type: "document", name: document[0] });
      }
    });
    return artifacts;
  }

  function buildSubprocessNodeNotes(graph, graphEdges) {
    if (!includeAvgTime && !includeFrequency) return null;
    const notes = {};
    const labels = new Set(
      graphEdges
        .flatMap(([from, to]) => [from, to])
        .filter((name) => name !== "Start" && name !== "End"),
    );
    labels.forEach((label) => {
      const metrics = graph.activityKpis?.[label] || {};
      const details = [];
      if (includeAvgTime) {
        const occurrenceRate = Number(metrics.occurrence_rate);
        const timePerCase = Number(metrics.time_per_case);
        const averageTime =
          Number.isFinite(timePerCase) &&
          Number.isFinite(occurrenceRate) &&
          occurrenceRate > 0
            ? timePerCase / occurrenceRate
            : NaN;
        details.push(
          `Avg. Time: ${Number.isFinite(averageTime) ? formatTimeDelta(averageTime) : "-"}`,
        );
      }
      if (includeFrequency) {
        const frequency = Number(metrics.occurrence_rate);
        details.push(
          `Frequency: ${Number.isFinite(frequency) ? `${Math.round(frequency * 100)}%` : "-"}`,
        );
      }
      notes[label] = details.join("\n");
    });
    return notes;
  }

  async function buildSubprocesses(
    disableLlm = false,
    drawTasksOverride = null,
  ) {
    if (
      !(includeL4Subprocesses || (drawMode && drawTasksOverride)) ||
      !idproject
    )
      return null;
    const catalog = await getSubtaskCatalog();
    const selectedNodes = drawMode
      ? (drawTasksOverride || selectedDrawTasks).map((task) => ({
          id: task.id,
          label: task.name,
          parentNode: task.teams?.[0],
          catalogTask: task,
        }))
      : activityNodes.filter((node) =>
          selectedActivitySet.has(node.label || node.id),
        );
    return (
      await Promise.all(
        selectedNodes.map(async (node) => {
          const name = node.label || node.id;
          const candidates = node.catalogTask
            ? [node.catalogTask]
            : catalog.filter(
                (item) =>
                  normalizedName(item.name || item.task || item.l4) ===
                  normalizedName(name),
              );
          const match =
            node.catalogTask ||
            candidates.find((item) =>
              (item.teams || []).includes(node.parentNode),
            ) ||
            candidates[0];
          if (!match) return null;
          const graph = await getTaskGraph(match);
          const sortedSubprocessVariants = [...(graph.variants || [])].sort(
            (a, b) => Number(b.count || 0) - Number(a.count || 0),
          );
          const selectedSubprocessVariants = selectTopVariants(
            sortedSubprocessVariants,
            effectiveTopKCount,
            effectiveTopKCoverage,
            sortedSubprocessVariants,
          );
          const subprocessActivities = new Set(
            (graph.nodes || [])
              .map((graphNode) => graphNode.id)
              .filter((label) => label && label !== "Start" && label !== "End"),
          );
          const graphEdges =
            graphMode === "variants" && selectedSubprocessVariants.length
              ? buildVariantEdges(
                  selectedSubprocessVariants,
                  new Set(selectedSubprocessVariants.flatMap(variantPath)),
                )
              : buildSelectedEdges(
                  "transitions",
                  graph.edges || [],
                  subprocessActivities,
                  [],
                  effectiveTopKCount,
                  effectiveTopKCoverage,
                  sortedSubprocessVariants,
                );
          return graphEdges.length
            ? {
                name,
                team_name: match.teams?.[0] || node.parentNode || null,
                edges: graphEdges,
                insert_gateways_on_split_and_merge: false,
                add_xor_labels_via_llm: !disableLlm,
                process_artifacts: buildSubprocessArtifacts(graph, graphEdges),
                node_notes: buildSubprocessNodeNotes(graph, graphEdges),
              }
            : null;
        }),
      )
    ).filter(Boolean);
  }

  async function generateXml(disableLlm = false) {
    const subprocesses = await buildSubprocesses(disableLlm);
    const processArtifacts = drawMode
      ? await buildDrawArtifacts()
      : buildArtifacts();
    const throughputTimes =
      !drawMode && includeThroughputTime
        ? await buildCaseThroughputTimes()
        : [];
    const response = await fetchWithSession("/api/damprojects/generateBPMN", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        edges: selectedEdges,
        task_name: taskName,
        team_name: teamName,
        add_xor_labels_via_llm: !disableLlm,
        insert_gateways_on_split_and_merge: false,
        process_artifacts: processArtifacts,
        node_notes: drawMode ? buildDrawNodeNotes() : buildNodeNotes(),
        subprocesses,
        throughput_times: throughputTimes,
      }),
    });
    if (!response.ok)
      throw new Error(`BPMN generation failed with status ${response.status}`);
    return response.text();
  }

  async function buildCaseThroughputTimes() {
    const selectedPairs = new Set(
      selectedEdges.map(([from, to]) => `${from}\u0000${to}`),
    );
    const selectedRecordType =
      recordTypes.find((item) => item.name === throughputRecordType) ||
      recordTypes[0];
    let sourceEdges = edges;
    if (selectedRecordType?.ranges?.length) {
      const params = new URLSearchParams({
        caseids: JSON.stringify(selectedRecordType.ranges),
        startnodes: JSON.stringify(
          selectedRecordType.startNodes || selectedStartNodes || [],
        ),
      });
      const response = await fetchWithSession(
        `/api/damprojects/case-id-graphs/${idproject}?${params.toString()}`,
        { method: "GET" },
      );
      if (!response.ok)
        throw new Error(
          `Could not load throughput times for ${selectedRecordType.name}.`,
        );
      sourceEdges = (await response.json()).edges || [];
    }
    return sourceEdges
      .filter(
        (edge) =>
          selectedPairs.has(`${edge.from}\u0000${edge.to}`) &&
          Number(edge.averageDurationSeconds) > 0,
      )
      .map((edge) => ({
        from: edge.from,
        to: edge.to,
        averageDurationSeconds: Number(edge.averageDurationSeconds),
      }));
  }

  async function refreshPreview() {
    if (!open || !selectedEdges.length) {
      previewXml = "";
      previewError = selectedEdges.length
        ? ""
        : "Select tasks (L4) or variants to generate a preview.";
      return;
    }
    const request = ++previewRequest;
    previewLoading = true;
    previewError = "";
    try {
      const xml = await generateXml(true);
      if (request === previewRequest) previewXml = xml;
    } catch (error) {
      if (request === previewRequest)
        previewError = error.message || "Failed to generate BPMN preview.";
    } finally {
      if (request === previewRequest) previewLoading = false;
    }
  }

  function close() {
    clearTimeout(previewTimer);
    previewRequest += 1;
    dispatch("close");
  }

  async function startDrawProcess(event) {
    previewError = "";
    drawSaved = false;
    drawProcessName = event.detail.name || "Generated Process";
    drawRecordTaskIds = [];
    drawSourceGraph = null;
    await inlineModeler?.initializeProcess(event.detail.name);
  }

  async function loadDrawProcess(event) {
    previewError = "";
    drawSaved = true;
    drawProcessName = event.detail.drawing.name || "BPMN Model";
    drawRecordTaskIds = [];
    drawSourceGraph = event.detail.drawing.graph || null;
    await inlineModeler?.replaceXML(event.detail.drawing.xml);
    drawPostprocessed = true;
    drawSaved = true;
  }

  async function startDrawRecordType(event) {
    const recordType = event.detail.recordType;
    previewError = "";
    previewLoading = true;
    drawSaved = false;
    drawProcessName = recordType.name || "Object Process";
    try {
      const params = new URLSearchParams({
        caseids: JSON.stringify(recordType.ranges || []),
        startnodes: JSON.stringify(recordType.startNodes || []),
      });
      previewLoadingText = `Loading ${recordType.name}`;
      const graphResponse = await fetchWithSession(
        `/api/damprojects/case-id-graphs/${idproject}?${params.toString()}`,
        { method: "GET" },
      );
      if (!graphResponse.ok)
        throw new Error(`Could not load Object Type ${recordType.name}.`);
      const graph = await graphResponse.json();
      drawSourceGraph = { nodes: graph.nodes || [], edges: graph.edges || [] };
      const graphActivities = new Set(
        (graph.edges || [])
          .flatMap((edge) => [edge.from, edge.to])
          .filter((name) => name && name !== "Start" && name !== "End"),
      );
      const graphEdges = buildSelectedEdges(
        "transitions",
        graph.edges || [],
        graphActivities,
        [],
        effectiveTopKCount,
        effectiveTopKCoverage,
        graph.variants || [],
      );
      if (!graphEdges.length)
        throw new Error(
          `No recorded flow is available for ${recordType.name}.`,
        );
      const response = await fetchWithSession("/api/damprojects/generateBPMN", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          edges: graphEdges,
          task_name: drawProcessName,
          add_xor_labels_via_llm: false,
          insert_gateways_on_split_and_merge: false,
        }),
      });
      if (!response.ok)
        throw new Error(
          `Could not generate the initial BPMN model for ${recordType.name}.`,
        );
      const taskNames = new Set(
        (graph.nodes || [])
          .flatMap((node) => [node.id, node.label])
          .filter(Boolean),
      );
      drawRecordTaskIds = subtaskCatalog
        .filter((task) => taskNames.has(task.name))
        .map((task) => task.id);
      await inlineModeler?.replaceXML(await response.text());
      drawPostprocessed = true;
      event.detail.complete?.(true);
    } catch (error) {
      previewError = error.message || "Failed to create the Object Type model.";
      event.detail.complete?.(false);
    } finally {
      previewLoading = false;
      // Zurueck auf die allgemeine Meldung, sonst steht der Objektname spaeter
      // ueber einem ganz anderen Vorgang.
      previewLoadingText = "Generating BPMN Export";
    }
  }

  async function addDrawActivities(event) {
    inlineModeler?.addActivities(
      event.detail.tasks,
      event.detail.mode,
      event.detail,
    );
    if (!["sequence", "conditional", "parallel"].includes(event.detail.mode))
      return;
    drawPostprocessed = false;
    try {
      // The action has now created all activity, gateway, and sequence-flow
      // elements for this section. Format that complete XML in the preview.
      await tick();
      await postprocessDrawXml(true);
    } catch (error) {
      previewError =
        error.message || "Failed to format the BPMN after adding activities.";
    }
  }

  function addDrawElement(event) {
    inlineModeler?.addGateway(event.detail.type, event.detail.name);
  }

  async function finishDrawProcess() {
    inlineModeler?.finishProcess();
    drawPostprocessed = false;
    try {
      await tick();
      await postprocessDrawXml(true);
    } catch (error) {
      previewError = error.message || "Failed to format the completed BPMN.";
    }
  }

  /**
   * Zeigt die Angaben sofort im Modell, ohne den Ablauf abzuschliessen.
   * Ohne das passierte beim Setzen der Haken sichtbar nichts - die
   * Anreicherung kam erst bei "Apply and complete".
   */
  function previewDrawInformation(event) {
    return applyDrawInformation(event, { preview: true });
  }

  async function applyDrawInformation(event, { preview = false } = {}) {
    const {
      flags,
      taskIds,
      recordType,
      thresholds = {},
      manualTasks = [],
    } = event.detail;
    // Die Vollversion laedt Kennzahlen nach und laesst das Backend layouten -
    // das dauert sichtbar. Die Vorschau nebenher bleibt still.
    if (!preview) {
      previewLoadingText = "Generating BPMN Export";
      previewLoading = true;
    }
    // Format und Ausrichtung kommen aus dem letzten Schritt des Assistenten.
    exportFormat = event.detail.exportFormat || "standard";
    orientation = event.detail.orientation || "horizontal";
    bicGenerateTexts = Boolean(event.detail.generateTexts);
    const effectiveTaskIds = [...new Set([...taskIds, ...drawRecordTaskIds])];
    const tasks = [
      ...subtaskCatalog.filter((task) => effectiveTaskIds.includes(task.id)),
      ...manualTasks,
    ];
    try {
      const information = await Promise.all(
        tasks.map(async (task) => {
          const details = [];
          if (flags.avgTime) {
            const averageTime = Number(task.avg_time_seconds);
            details.push(
              `Avg. Time: ${Number.isFinite(averageTime) ? formatTimeDelta(averageTime) : "-"}`,
            );
          }
          if (flags.frequency) {
            const frequency = Number(task.monthly_frequency);
            details.push(
              `Frequency: ${Number.isFinite(frequency) ? `${Math.round(frequency)} / month` : "-"}`,
            );
          }
          const databases = new Map();
          const files = new Map();
          if (task.manual) {
            (task.databases || []).forEach((name) => databases.set(name, 1));
            (task.files || []).forEach((name) => files.set(name, 1));
          } else if (flags.databases || flags.files) {
            const graph = await getTaskGraph(task);
            Object.values(graph.activityKpis || {}).forEach((metrics) => {
              Object.entries(metrics.apps || {}).forEach(([name, share]) =>
                databases.set(
                  name,
                  Math.max(databases.get(name) || 0, Number(share || 0)),
                ),
              );
              Object.entries(metrics.top_windowtitles || {}).forEach(
                ([name, share]) =>
                  files.set(
                    name,
                    Math.max(files.get(name) || 0, Number(share || 0)),
                  ),
              );
            });
          }
          return {
            task: task.name,
            note: details.join("\n"),
            databases: flags.databases
              ? [...databases]
                  .filter(
                    ([, share]) =>
                      share * 100 >= Number(thresholds.systems || 0),
                  )
                  .sort((a, b) => b[1] - a[1])
                  .map(([name]) => name)
              : [],
            files: flags.files
              ? [...files]
                  .filter(
                    ([, share]) => share * 100 >= Number(thresholds.files || 0),
                  )
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 1)
                  .map(([name]) => name)
              : [],
          };
        }),
      );
      inlineModeler?.applyTaskInformation(information);
      // In der Vorschau endet es hier: Durchlaufzeiten, Teilprozesse und das
      // Backend-Layout gehoeren zum Abschluss, nicht zum Mitdenken nebenher.
      if (preview) return;
      if (flags.throughputTime) {
        let transitions = edges;
        if (recordType?.ranges?.length) {
          const params = new URLSearchParams({
            caseids: JSON.stringify(recordType.ranges),
            startnodes: JSON.stringify(
              recordType.startNodes || selectedStartNodes || [],
            ),
          });
          const response = await fetchWithSession(
            `/api/damprojects/case-id-graphs/${idproject}?${params.toString()}`,
            { method: "GET" },
          );
          if (!response.ok)
            throw new Error(
              `Could not load throughput times for ${recordType.name}.`,
            );
          transitions = (await response.json()).edges || [];
        }
        inlineModeler?.applyThroughputTimes(transitions);
      }
      const subprocesses = flags.addSubprocess
        ? await buildSubprocesses(
            true,
            tasks.filter((task) => !task.manual),
          )
        : [];
      await postprocessDrawXml(true, {
        subprocesses: subprocesses || [],
        emptySubprocessNames: flags.addSubprocess
          ? manualTasks.map((task) => task.name)
          : [],
      });
    } catch (error) {
      previewError = error.message || "Failed to attach task information.";
    } finally {
      if (!preview) previewLoading = false;
    }
  }

  async function postprocessDrawXml(updateModeler = false, options = {}) {
    previewLoading = true;
    try {
      // Das Backend-Layout arbeitet links-nach-rechts; gedreht gaebe es Unsinn.
      const xml = await inlineModeler?.saveHorizontalXML();
      if (!xml) throw new Error("No BPMN model is available for formatting.");
      const response = await fetchWithSession(
        "/api/damprojects/postprocessBPMN",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            xml,
            task_name: drawProcessName,
            subprocesses: options.subprocesses || [],
            empty_subprocess_names: options.emptySubprocessNames || [],
          }),
        },
      );
      if (!response.ok)
        throw new Error(
          `BPMN formatting failed with status ${response.status}`,
        );
      const formattedXml = await response.text();
      if (updateModeler) {
        await inlineModeler?.replaceXML(formattedXml);
        drawPostprocessed = true;
      }
      return formattedXml;
    } finally {
      previewLoading = false;
    }
  }

  async function exportBpmn() {
    if (
      generating ||
      (drawMode ? !drawDiagramState.started : !selectedEdges.length)
    )
      return;
    generating = true;
    try {
      // Horizontal weitergeben: der Viewer dreht anhand von orientation selbst.
      const xml = drawMode
        ? drawPostprocessed
          ? await inlineModeler?.saveHorizontalXML()
          : await postprocessDrawXml(false)
        : await generateXml(false);
      if (!xml) throw new Error("No BPMN model is available for export.");
      let frameworkPath = [];
      try {
        frameworkPath = await getSaveFrameworkPath();
      } catch (error) {
        console.warn("Could not resolve BPMN framework path:", error);
      }
      dispatch("export", {
        xml,
        frameworkPath,
        name: drawMode ? drawProcessName : taskName,
        activityMetrics: buildActivityMetrics(),
        exportFormat,
        orientation,
        bicIdentifier,
        bicValidTo,
        bicChangeLog,
        bicGenerateTexts,
      });
    } catch (error) {
      previewError = error.message || "Failed to generate BPMN.";
    } finally {
      generating = false;
    }
  }

  function openSaveDrawing() {
    saveDrawingName = drawProcessName || "BPMN Model";
    saveDrawingOpen = true;
  }

  async function saveDrawBpmn() {
    if (generating || !drawDiagramState.started) return;
    generating = true;
    previewError = "";
    try {
      // Gespeichert wird horizontal, damit ein spaeteres Laden frei drehen kann.
      const xml = drawPostprocessed
        ? await inlineModeler?.saveHorizontalXML()
        : await postprocessDrawXml(false);
      if (!xml) throw new Error("No BPMN model is available for saving.");
      const name = saveDrawingName.trim();
      if (!name) throw new Error("Enter a name for the model.");
      dispatch("save", { xml, name, graph: drawSourceGraph });
      drawProcessName = name;
      drawSaved = true;
      saveDrawingOpen = false;
    } catch (error) {
      previewError = error.message || "Failed to save BPMN model.";
    } finally {
      generating = false;
    }
  }

  function toggleSection(section) {
    sectionsOpen = { ...sectionsOpen, [section]: !sectionsOpen[section] };
  }

  onDestroy(() => clearTimeout(previewTimer));
</script>

<div class="BPMN-modal-wrapper">
  <Modal
    {open}
    modalHeading={`BPMN Export (${drawMode ? "Draw" : "Object"})`}
    subtitle={drawMode
      ? "Build a BPMN model step by step with Pax AI."
      : "Configure how the object BPMN model should be generated."}
    fitcontent
    hideFooter
    preventCloseOnClickOutside
    width={1400}
    on:close={close}
  >
    <div class="modal_content bpmn_modal_layout">
      {#if drawMode}
        <div class="draw_assistant_column">
          <BpmnDrawAssistant
            catalog={subtaskCatalog}
            framework={drawFramework}
            loading={drawCatalogLoading}
            projectId={idproject}
            diagramState={drawDiagramState}
            {recordTypes}
            drawings={savedDrawings}
            on:process={startDrawProcess}
            on:loadDrawing={loadDrawProcess}
            on:recordType={startDrawRecordType}
            on:activities={addDrawActivities}
            on:element={addDrawElement}
            on:finish={finishDrawProcess}
            on:information={applyDrawInformation}
            on:information-preview={previewDrawInformation}
            on:output={(event) => {
              exportFormat = event.detail.exportFormat;
              orientation = event.detail.orientation;
              bicGenerateTexts = Boolean(event.detail.generateTexts);
            }}
            on:export={exportBpmn}
            on:save={openSaveDrawing}
            on:reset={() => inlineModeler?.resetDiagram()}
          />
        </div>
      {:else}
        <div class="bpmn_settings_column">
          {#if drawMode}
            <section class="bpmn_collapse_section">
              <div class="bpmn_collapse_body draw_level_options">
                <strong>L4 subprocesses</strong>
                <p>
                  Choose whether selected activities include their detailed L4
                  subprocess.
                </p>
                <div class="bpmn_segmented draw_segmented">
                  <button
                    type="button"
                    class:active={!includeL4Subprocesses}
                    on:click={() => (includeL4Subprocesses = false)}
                    >Without L4</button
                  >
                  <button
                    type="button"
                    class:active={includeL4Subprocesses}
                    on:click={() => (includeL4Subprocesses = true)}
                    >With L4</button
                  >
                </div>
              </div>
            </section>
          {/if}
          {#if !drawMode}<section class="bpmn_collapse_section">
              <button
                type="button"
                class="bpmn_collapse_header"
                on:click={() => toggleSection("topK")}
                ><span>Path Selection</span><span
                  >{sectionsOpen.topK ? "^" : "v"}</span
                ></button
              >
              {#if sectionsOpen.topK}<div class="bpmn_collapse_body">
                  <p>
                    Define how top {graphMode === "variants"
                      ? "variants"
                      : "paths"} are selected for the model.
                  </p>
                  <div class="bpmn_segmented">
                    {#each ["simple", "balanced", "custom"] as mode}<button
                        type="button"
                        class:active={topKMode === mode}
                        on:click={() => (topKMode = mode)}
                        >{mode[0].toUpperCase() + mode.slice(1)}</button
                      >{/each}
                  </div>
                  <div class="bpmn_stepper_grid">
                    <div
                      class:disabled={topKMode !== "custom"}
                      class="bpmn_stepper"
                    >
                      <button
                        type="button"
                        disabled={topKMode !== "custom" || topKCount <= 1}
                        on:click={() => (topKCount -= 1)}>-</button
                      >
                      <div>
                        <span
                          >{graphMode === "variants"
                            ? "Variants"
                            : "Paths"}</span
                        ><strong>{effectiveTopKCount}</strong>
                      </div>
                      <button
                        type="button"
                        disabled={topKMode !== "custom"}
                        on:click={() => (topKCount += 1)}>+</button
                      >
                    </div>
                    <div
                      class:disabled={topKMode !== "custom"}
                      class="bpmn_stepper"
                    >
                      <button
                        type="button"
                        disabled={topKMode !== "custom" || topKCoverage <= 0}
                        on:click={() =>
                          (topKCoverage = Math.max(0, topKCoverage - 5))}
                        >-</button
                      >
                      <div>
                        <span>Coverage</span><strong
                          >{effectiveTopKCoverage}%</strong
                        >
                      </div>
                      <button
                        type="button"
                        disabled={topKMode !== "custom" || topKCoverage >= 100}
                        on:click={() =>
                          (topKCoverage = Math.min(100, topKCoverage + 5))}
                        >+</button
                      >
                    </div>
                  </div>
                </div>{/if}
            </section>{/if}

          <section class="bpmn_collapse_section">
            <button
              type="button"
              class="bpmn_collapse_header"
              on:click={() => toggleSection("selection")}
              ><span>Task (L4) Selection</span><span
                >{sectionsOpen.selection ? "^" : "v"}</span
              ></button
            >
            {#if sectionsOpen.selection}<div class="bpmn_collapse_body">
                <p>
                  Choose the {graphMode === "variants" && !drawMode
                    ? "variants"
                    : "tasks (L4)"} that should be part of the BPMN model.
                </p>
                {#if graphMode === "variants" && !drawMode}
                  <div class="selection_count">
                    <strong
                      >{selectedVariants.length} / {variants.length}</strong
                    > selected
                  </div>
                  <div class="selection_actions">
                    <button
                      type="button"
                      on:click={() =>
                        (modalVariantIds = variants.map(
                          (variant) => variant.id,
                        ))}>Select all</button
                    ><button
                      type="button"
                      on:click={() => (modalVariantIds = [])}
                      >Deselect all</button
                    >
                  </div>
                  <div class="selection_list">
                    {#each sortedVariants as variant}<label
                        ><input
                          type="checkbox"
                          checked={modalVariantIds.includes(variant.id)}
                          on:change={() =>
                            (modalVariantIds = modalVariantIds.includes(
                              variant.id,
                            )
                              ? modalVariantIds.filter(
                                  (id) => id !== variant.id,
                                )
                              : [...modalVariantIds, variant.id])}
                        /><span
                          >Variant {variant.name}
                          <small
                            >{Math.round(Number(variant.coverage || 0) * 100)}%
                            cases</small
                          ></span
                        ></label
                      >{/each}
                  </div>
                {:else if drawMode}
                  <div class="draw_task_search">
                    <img src="/img/assistant/icons/search.svg" alt="" />
                    <input
                      type="search"
                      bind:value={drawTaskSearch}
                      placeholder="Search for Task"
                      aria-label="Search for Task"
                    />
                  </div>
                  <div class="draw_grouping">
                    <span>Group by</span>
                    {#each ["Team", "L1", "L2", "L3"] as grouping}
                      <button
                        type="button"
                        class:active={drawTaskGrouping === grouping}
                        on:click={() => {
                          drawTaskGrouping = grouping;
                          taskSelectionPage = 1;
                        }}>{grouping}</button
                      >
                    {/each}
                  </div>
                  <div class="selection_count">
                    <strong
                      >{selectedDrawTaskIds.length} / {subtaskCatalog.length}</strong
                    > selected
                  </div>
                  <div class="selection_actions">
                    <button
                      type="button"
                      disabled={drawCatalogLoading}
                      on:click={() =>
                        (selectedDrawTaskIds = subtaskCatalog.map(
                          (task) => task.id,
                        ))}>Select all</button
                    ><button
                      type="button"
                      disabled={drawCatalogLoading}
                      on:click={() => (selectedDrawTaskIds = [])}
                      >Deselect all</button
                    >
                  </div>
                  <div class="task_selection_table draw_task_table">
                    <div class="task_selection_header">
                      <span>Include</span><span>Task (L4)</span><span
                        >Recordings</span
                      >
                    </div>
                    {#if drawCatalogLoading}
                      <div class="draw_task_empty">
                        Loading project tasks...
                      </div>
                    {:else if !visibleTaskRows.length}
                      <div class="draw_task_empty">No matching L4 tasks.</div>
                    {:else}
                      {#each visibleDrawTaskGroups as group}
                        <div class="draw_task_group">{group.name}</div>
                        {#each group.tasks as task}
                          <label class="task_selection_row">
                            <input
                              type="checkbox"
                              checked={selectedDrawTaskIds.includes(task.id)}
                              on:change={() =>
                                (selectedDrawTaskIds =
                                  selectedDrawTaskIds.includes(task.id)
                                    ? selectedDrawTaskIds.filter(
                                        (id) => id !== task.id,
                                      )
                                    : [...selectedDrawTaskIds, task.id])}
                            />
                            <span title={task.name}>{task.name}</span>
                            <span>{Number(task.nrecordings || 0)}</span>
                          </label>
                        {/each}
                      {/each}
                    {/if}
                  </div>
                  <div class="task_selection_pagination">
                    <span
                      >{taskRows.length
                        ? (taskSelectionPage - 1) * taskSelectionPageSize + 1
                        : 0}-{Math.min(
                        taskSelectionPage * taskSelectionPageSize,
                        taskRows.length,
                      )} of {taskRows.length} items</span
                    >
                    <select
                      bind:value={taskSelectionPage}
                      aria-label="Task selection page"
                      >{#each Array(taskSelectionPageCount) as _, index}<option
                          value={index + 1}>{index + 1}</option
                        >{/each}</select
                    >
                    <span>of {taskSelectionPageCount} pages</span>
                    <button
                      type="button"
                      disabled={taskSelectionPage <= 1}
                      on:click={() => (taskSelectionPage -= 1)}>&lt;</button
                    >
                    <button
                      type="button"
                      disabled={taskSelectionPage >= taskSelectionPageCount}
                      on:click={() => (taskSelectionPage += 1)}>&gt;</button
                    >
                  </div>
                {:else}
                  <div class="selection_count">
                    <strong
                      >{selectedActivities.length} / {activityLabels.length}</strong
                    > selected
                  </div>
                  <div class="selection_actions">
                    <button
                      type="button"
                      on:click={() =>
                        (selectedActivities = [...activityLabels])}
                      >Select all</button
                    ><button
                      type="button"
                      on:click={() => (selectedActivities = [])}
                      >Deselect all</button
                    >
                  </div>
                  <div class="task_selection_table">
                    <div class="task_selection_header">
                      <span>Include</span><span>Task (L4)</span><span
                        >Cases <i
                          title="Fraction of cases that include at least one occurrence of the task (L4)"
                          >i</i
                        ></span
                      >
                    </div>
                    {#each visibleTaskRows as task}
                      <label class="task_selection_row">
                        <input
                          type="checkbox"
                          checked={selectedActivities.includes(task.label)}
                          on:change={() =>
                            (selectedActivities = selectedActivities.includes(
                              task.label,
                            )
                              ? selectedActivities.filter(
                                  (item) => item !== task.label,
                                )
                              : [...selectedActivities, task.label])}
                        />
                        <span title={task.label}>{task.label}</span>
                        <span
                          >{Number.isFinite(task.cases)
                            ? `${(task.cases * 100).toFixed(1)}%`
                            : "-"}</span
                        >
                      </label>
                    {/each}
                  </div>
                  <div class="task_selection_pagination">
                    <span
                      >{taskRows.length
                        ? (taskSelectionPage - 1) * taskSelectionPageSize + 1
                        : 0}–{Math.min(
                        taskSelectionPage * taskSelectionPageSize,
                        taskRows.length,
                      )} of {taskRows.length} items</span
                    >
                    <select
                      bind:value={taskSelectionPage}
                      aria-label="Task selection page"
                      >{#each Array(taskSelectionPageCount) as _, index}<option
                          value={index + 1}>{index + 1}</option
                        >{/each}</select
                    >
                    <span>of {taskSelectionPageCount} pages</span>
                    <button
                      type="button"
                      disabled={taskSelectionPage <= 1}
                      on:click={() => (taskSelectionPage -= 1)}>‹</button
                    >
                    <button
                      type="button"
                      disabled={taskSelectionPage >= taskSelectionPageCount}
                      on:click={() => (taskSelectionPage += 1)}>›</button
                    >
                  </div>
                {/if}
              </div>{/if}
          </section>

          {#if !drawMode && showGatewayBehaviorSelection}<section
              class="bpmn_collapse_section"
            >
              <button
                type="button"
                class="bpmn_collapse_header"
                on:click={() => toggleSection("gateway")}
                ><span>Gateway Behavior</span><span
                  >{sectionsOpen.gateway ? "^" : "v"}</span
                ></button
              >
              {#if sectionsOpen.gateway}<div class="bpmn_collapse_body">
                  <p>Define how gateways are represented in the model.</p>
                  <div class="option_grid">
                    <label
                      ><input
                        type="radio"
                        bind:group={gatewayBehavior}
                        value="split_xor"
                      /><span
                        ><strong>Split XOR</strong><small
                          >Create separate XOR gateways.</small
                        ></span
                      ></label
                    ><label
                      ><input
                        type="radio"
                        bind:group={gatewayBehavior}
                        value="split_merge_xor"
                      /><span
                        ><strong>Split and merge XOR</strong><small
                          >Merge XOR gateways where possible.</small
                        ></span
                      ></label
                    >
                  </div>
                </div>{/if}
            </section>{/if}

          <section class="bpmn_collapse_section">
            <button
              type="button"
              class="bpmn_collapse_header"
              on:click={() => toggleSection("artifacts")}
              ><span>Additional Elements</span><span
                >{sectionsOpen.artifacts ? "^" : "v"}</span
              ></button
            >
            {#if sectionsOpen.artifacts}<div class="bpmn_collapse_body">
                <p>Choose additional model artifacts.</p>
                <div class="option_grid">
                  {#each [["IT-Systems (databases)", "Show IT systems interacting with tasks (L4).", "database"], ["Files (Documents)", "Show documents connected to tasks (L4).", "document"], ["Avg time", "Show average time below task (L4) names.", "time"], ["Frequency", "Show task (L4) frequency below task names.", "frequency"], ["Subprocesses", "Add the recorded L4 detail flow inside activities.", "subprocess"], ["Avg. throughput time", "Add BPMN timer events for recorded transition times.", "throughput"]] as option}<label
                      ><input
                        type="checkbox"
                        checked={option[2] === "database"
                          ? includeDatabases
                          : option[2] === "document"
                            ? includeDocuments
                            : option[2] === "time"
                              ? includeAvgTime
                              : option[2] === "frequency"
                                ? includeFrequency
                                : option[2] === "subprocess"
                                  ? includeL4Subprocesses
                                  : includeThroughputTime}
                        on:change={() =>
                          option[2] === "database"
                            ? (includeDatabases = !includeDatabases)
                            : option[2] === "document"
                              ? (includeDocuments = !includeDocuments)
                              : option[2] === "time"
                                ? (includeAvgTime = !includeAvgTime)
                                : option[2] === "frequency"
                                  ? (includeFrequency = !includeFrequency)
                                  : option[2] === "subprocess"
                                    ? (includeL4Subprocesses =
                                        !includeL4Subprocesses)
                                    : (includeThroughputTime =
                                        !includeThroughputTime)}
                      /><span
                        ><strong>{option[0]}</strong><small>{option[1]}</small
                        ></span
                      ></label
                    >{/each}
                </div>
                {#if includeThroughputTime && recordTypes.length > 1}<label
                    class="throughput_record_type"
                    ><span>Object type for throughput times</span><select
                      bind:value={throughputRecordType}
                      >{#each recordTypes as recordType}<option
                          value={recordType.name}>{recordType.name}</option
                        >{/each}</select
                    ></label
                  >{/if}
                <div class="bpmn_segmented">
                  {#each ["simple", "balanced", "custom"] as mode}<button
                      type="button"
                      class:active={artifactMode === mode}
                      on:click={() => (artifactMode = mode)}
                      >{mode[0].toUpperCase() + mode.slice(1)}</button
                    >{/each}
                </div>
                <div class="bpmn_stepper_grid">
                  <div
                    class:disabled={!includeDatabases ||
                      artifactMode !== "custom"}
                    class="bpmn_stepper"
                  >
                    <button
                      type="button"
                      disabled={!includeDatabases ||
                        artifactMode !== "custom" ||
                        systemMinFrequency <= 0}
                      on:click={() =>
                        (systemMinFrequency = Math.max(
                          0,
                          systemMinFrequency - 5,
                        ))}>-</button
                    >
                    <div>
                      <span>Systems</span><strong>{effectiveSystemMin}%</strong>
                    </div>
                    <button
                      type="button"
                      disabled={!includeDatabases ||
                        artifactMode !== "custom" ||
                        systemMinFrequency >= 100}
                      on:click={() =>
                        (systemMinFrequency = Math.min(
                          100,
                          systemMinFrequency + 5,
                        ))}>+</button
                    >
                  </div>
                  <div
                    class:disabled={!includeDocuments ||
                      artifactMode !== "custom"}
                    class="bpmn_stepper"
                  >
                    <button
                      type="button"
                      disabled={!includeDocuments ||
                        artifactMode !== "custom" ||
                        fileMinFrequency <= 0}
                      on:click={() =>
                        (fileMinFrequency = Math.max(0, fileMinFrequency - 5))}
                      >-</button
                    >
                    <div>
                      <span>Files</span><strong>{effectiveFileMin}%</strong>
                    </div>
                    <button
                      type="button"
                      disabled={!includeDocuments ||
                        artifactMode !== "custom" ||
                        fileMinFrequency >= 100}
                      on:click={() =>
                        (fileMinFrequency = Math.min(
                          100,
                          fileMinFrequency + 5,
                        ))}>+</button
                    >
                  </div>
                </div>
              </div>{/if}
          </section>
        </div>
      {/if}
      <div class="bpmn_preview_column">
        {#if drawMode}
          <BpmnInlineModeler
            bind:this={inlineModeler}
            {orientation}
            loading={previewLoading || generating}
            loadingText={previewLoadingText}
            error={previewError}
            on:change={(event) => {
              drawDiagramState = event.detail;
              drawPostprocessed = false;
              drawSaved = false;
            }}
          />
        {:else}
          <BpmnInlinePreview
            bpmnXml={previewXml}
            loading={previewLoading || generating}
            loadingText={previewLoadingText}
            error={previewError}
          />
        {/if}
        <div class="bpmn_preview_actions">
          <Button
            kind="secondary"
            text="Cancel"
            on:click={close}
          />{#if drawMode}<Button
              kind="secondary"
              text={drawSaved ? "Saved" : "Save Model"}
              disabled={generating || !drawDiagramState.started}
              on:click={openSaveDrawing}
            />{/if}<Button
            kind="secondary"
            text={generating ? "Exporting..." : "Export BPMN"}
            icon={DOWNLOAD_ICON}
            iconLeft
            disabled={generating ||
              (drawMode ? !drawDiagramState.started : !selectedEdges.length)}
            on:click={exportBpmn}
          />
        </div>
      </div>
    </div>
  </Modal>
  {#if saveDrawingOpen}<div
      class="save_drawing_backdrop"
      role="presentation"
      on:click|self={() => (saveDrawingOpen = false)}
    >
      <form class="save_drawing_dialog" on:submit|preventDefault={saveDrawBpmn}>
        <header>
          <div>
            <strong>Save BPMN model</strong><span
              >Enter a name for this model.</span
            >
          </div>
          <button
            type="button"
            aria-label="Close"
            on:click={() => (saveDrawingOpen = false)}>×</button
          >
        </header>
        <label
          ><span>Model name</span><input
            bind:value={saveDrawingName}
            placeholder="e.g. Invoice process"
          /></label
        >
        <footer>
          <Button
            kind="secondary"
            text="Cancel"
            on:click={() => (saveDrawingOpen = false)}
          /><Button
            kind="primary"
            text={generating ? "Saving..." : "Save Model"}
            disabled={!saveDrawingName.trim() || generating}
            on:click={saveDrawBpmn}
          />
        </footer>
      </form>
    </div>{/if}
</div>

<style>
  :global(.BPMN-modal-wrapper .bx--modal-container) {
    width: min(99vw, 112rem) !important;
    max-width: 112rem !important;
  }
  :global(.BPMN-modal-wrapper .custom-modal .bx--modal-content),
  :global(.BPMN-modal-wrapper .modal_wrapper),
  :global(.BPMN-modal-wrapper .modal_content) {
    width: 100% !important;
    max-width: none !important;
  }
  :global(.BPMN-modal-wrapper .custom-modal .bx--modal-content) {
    padding-top: 0.75rem;
  }
  :global(.BPMN-modal-wrapper .divider) {
    margin-bottom: 1.25rem;
  }
  .bpmn_modal_layout {
    display: grid;
    grid-template-columns: 32rem minmax(0, 1fr);
    align-items: stretch;
    width: 100%;
    min-height: min(72vh, 52rem);
    gap: 1.25rem;
    overflow: hidden;
  }
  .bpmn_settings_column {
    width: 32rem;
    min-width: 32rem;
    max-width: 32rem;
    max-height: min(72vh, 52rem);
    overflow: auto;
    padding-right: 0.25rem;
  }
  .draw_assistant_column {
    width: 32rem;
    min-width: 32rem;
    max-width: 32rem;
  }
  :global(.BPMN-modal-wrapper .inline-preview),
  :global(.BPMN-modal-wrapper .inline-modeler) {
    border-color: var(--pax-black) !important;
  }
  .throughput_record_type {
    display: grid;
    gap: 0.4rem;
    margin-top: 0.75rem;
    font-size: 0.8rem;
    font-weight: 600;
  }
  .throughput_record_type select {
    height: 2.5rem;
    padding: 0 0.75rem;
    border: 1px solid #8d8d8d;
    border-radius: 0.35rem;
    background: #fff;
  }
  .bpmn_preview_column {
    position: relative;
    min-width: 0;
    width: 100%;
    min-height: min(72vh, 52rem);
  }
  .bpmn_preview_actions {
    position: absolute;
    right: 1rem;
    bottom: 1rem;
    z-index: 200;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .save_drawing_backdrop {
    position: fixed;
    inset: 0;
    z-index: 10050;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.42);
  }
  .save_drawing_dialog {
    width: min(28rem, calc(100vw - 2rem));
    overflow: hidden;
    border-radius: 0.55rem;
    background: #fff;
    box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.2);
  }
  .save_drawing_dialog header,
  .save_drawing_dialog footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1rem;
    border-bottom: 1px solid #e0e0e0;
  }
  .save_drawing_dialog header div {
    display: grid;
    gap: 0.2rem;
  }
  .save_drawing_dialog header span {
    color: #6f6f6f;
    font-size: 0.75rem;
  }
  .save_drawing_dialog header button {
    border: 0;
    background: transparent;
    font-size: 1.25rem;
  }
  .save_drawing_dialog label {
    display: grid;
    gap: 0.4rem;
    padding: 1rem;
    font-size: 0.8rem;
    font-weight: 700;
  }
  .save_drawing_dialog input {
    height: 2.6rem;
    padding: 0 0.75rem;
    border: 1px solid #8d8d8d;
    border-radius: 0.35rem;
  }
  .save_drawing_dialog footer {
    justify-content: flex-end;
    border-top: 1px solid #e0e0e0;
    border-bottom: 0;
  }
  .bpmn_collapse_section {
    border-bottom: 1px solid #d9d9d9;
  }
  .bpmn_collapse_header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 1rem 0.25rem;
    border: 0;
    background: transparent;
    font-weight: 700;
    cursor: pointer;
  }
  .bpmn_collapse_body {
    padding: 0 0.25rem 1rem;
  }
  .draw_level_options {
    width: 100%;
    box-sizing: border-box;
    padding: 1rem 0;
  }
  .draw_level_options > p {
    margin-top: 0.35rem;
  }
  .bpmn_collapse_body > p {
    margin: 0 0 0.75rem;
    color: var(--pax-grey);
    font-size: 0.78rem;
  }
  .bpmn_segmented {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    margin: 0.7rem 0;
    border: 1px solid #d9d9d9;
    border-radius: 0.4rem;
    overflow: hidden;
  }
  .bpmn_segmented.draw_segmented {
    width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .bpmn_segmented button {
    min-height: 2rem;
    border: 0;
    border-right: 1px solid #d9d9d9;
    background: #fff;
    cursor: pointer;
  }
  .bpmn_segmented button:last-child {
    border-right: 0;
  }
  .bpmn_segmented button.active {
    background: var(--pax-black);
    color: #fff;
  }
  .bpmn_stepper_grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }
  .bpmn_stepper {
    min-height: 2.45rem;
    display: grid;
    grid-template-columns: 2.35rem 1fr 2.35rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.4rem;
    overflow: hidden;
  }
  .bpmn_stepper > div {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    font-size: 0.76rem;
  }
  .bpmn_stepper button {
    border: 0;
    background: #fff;
    font-size: 1.1rem;
    cursor: pointer;
  }
  .bpmn_stepper button:first-child {
    border-right: 1px solid #d9d9d9;
  }
  .bpmn_stepper button:last-child {
    border-left: 1px solid #d9d9d9;
  }
  .bpmn_stepper.disabled {
    opacity: 0.45;
  }
  .selection_count {
    margin-bottom: 0.65rem;
    font-size: 0.76rem;
  }
  .selection_actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
  .selection_actions button {
    height: 1.8rem;
    border: 0;
    border-radius: 0.375rem;
    background: var(--pax-black);
    color: var(--pax-white);
    cursor: pointer;
  }
  .selection_actions button:disabled {
    opacity: 0.45;
    cursor: default;
  }
  .draw_task_search {
    position: relative;
    margin-bottom: 0.65rem;
  }
  .draw_task_search img {
    position: absolute;
    top: 50%;
    left: 0.65rem;
    width: 1rem;
    height: 1rem;
    transform: translateY(-50%);
  }
  .draw_task_search input {
    box-sizing: border-box;
    width: 100%;
    height: 2.5rem;
    padding: 0 0.75rem 0 2.2rem;
    border: 1px solid #8d8d8d;
    border-radius: 0.35rem;
    background: #fff;
  }
  .draw_task_search input:focus {
    border-color: var(--pax-black);
    outline: 1px solid var(--pax-black);
  }
  .draw_grouping {
    display: grid;
    grid-template-columns: auto repeat(4, 1fr);
    align-items: center;
    gap: 0.45rem;
    margin-bottom: 0.8rem;
    font-size: 0.72rem;
  }
  .draw_grouping button {
    height: 1.8rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.35rem;
    background: #fff;
    cursor: pointer;
  }
  .draw_grouping button.active {
    border-color: var(--pax-orange);
    color: var(--pax-orange);
    font-weight: 700;
  }
  .selection_list {
    max-height: 12rem;
    overflow-y: auto;
    border: 1px solid #e0e0e0;
  }
  .selection_list label {
    box-sizing: border-box;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.55rem;
    padding: 0.55rem 0.65rem;
    border-bottom: 1px solid #eee;
    font-size: 0.76rem;
  }
  .selection_list input {
    width: 1rem !important;
    min-width: 1rem;
    height: 1rem;
    flex: 0 0 1rem;
    margin: 0;
  }
  .selection_list span {
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .selection_list small {
    color: var(--pax-grey);
  }
  .task_selection_table {
    width: 100%;
    font-size: 0.76rem;
  }
  .task_selection_header,
  .task_selection_row {
    display: grid;
    grid-template-columns: 4.5rem minmax(0, 1fr) 4.75rem;
    align-items: center;
  }
  .task_selection_header {
    min-height: 1.65rem;
    padding: 0 0.55rem;
    border-radius: 0.4rem;
    background: var(--pax-black);
    color: var(--pax-white);
    font-weight: 700;
  }
  .task_selection_header i {
    width: 0.8rem;
    height: 0.8rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid currentColor;
    border-radius: 50%;
    font-size: 0.55rem;
    font-style: normal;
  }
  .task_selection_row {
    min-height: 1.65rem;
    padding: 0 0.55rem;
    border-bottom: 1px solid #d9d9d9;
    cursor: pointer;
  }
  .task_selection_row input {
    width: 1rem !important;
    height: 1rem;
    margin: 0;
    accent-color: var(--pax-black);
  }
  .task_selection_row span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .task_selection_row span:last-child {
    text-align: right;
  }
  .draw_task_group {
    padding: 0.4rem 0.55rem;
    border-bottom: 1px solid #d9d9d9;
    background: #f4f4f4;
    font-weight: 700;
  }
  .draw_task_empty {
    padding: 1rem 0.55rem;
    color: var(--pax-grey);
    text-align: center;
  }
  .task_selection_pagination {
    min-height: 3.2rem;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-size: 0.72rem;
  }
  .task_selection_pagination > span:first-child {
    margin-right: auto;
  }
  .task_selection_pagination select {
    width: 3.5rem;
    height: 2.4rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.35rem;
    background: #fff;
    text-align: center;
  }
  .task_selection_pagination button {
    width: 2rem;
    height: 2rem;
    border: 0;
    background: transparent;
    cursor: pointer;
  }
  .task_selection_pagination button:disabled {
    opacity: 0.35;
    cursor: default;
  }
  .option_grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.8rem 1.2rem;
  }
  .option_grid label {
    display: grid;
    grid-template-columns: 1rem 1fr;
    gap: 0.45rem;
    align-items: start;
  }
  .option_grid span {
    display: flex;
    flex-direction: column;
    gap: 0.12rem;
    font-size: 0.76rem;
  }
  .option_grid small {
    color: var(--pax-grey);
    font-size: 0.65rem;
  }
  @media (max-width: 900px) {
    .bpmn_modal_layout {
      grid-template-columns: 1fr;
      overflow-y: auto;
    }
    .bpmn_settings_column,
    .draw_assistant_column {
      width: 100%;
      min-width: 0;
      max-width: none;
    }
    .bpmn_preview_column {
      min-height: 28rem;
    }
  }
</style>
